import moment from 'moment';

import {apolloClient} from '../../../apolloClient';
import {
  AllQueuesQueryResult,
  DefaultResourceConfig,
  DefaultResourceConfigEdge,
  Entity,
  EntityQueuesQueryResult,
  Maybe,
  Project,
  RunQueueItemEdge,
  RunQueueItemInfoDetails,
} from '../../../generated/graphql';
import {RUNS_INFORMATION_QUERY} from '../../../state/graphql/launch';
import {runNamesFilter, toMongo} from '../../../util/filters';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../constants';
import {caseInsensitiveCompare} from '../hooks';
import {convertAgent} from '../utilData';
import {
  WANDB_ENTITY_NAME,
  WANDB_GLOBAL_CPU_QUEUE,
  WANDB_JOBS_PROJECT_NAME,
} from '../utils';
import {Agent, Json, ResourceType} from '../utilTypes';

// Map from a default resource config ID to its definition.
type DefaultResourceConfigMap = Record<string, DefaultResourceConfig>;

export type RunInfo = {
  id: string;
  name: string;
  displayName: string;
  state: string;
  createdAt: string;
  heartbeatAt: string | undefined;
};

// Map a run name to details about that run for display.
export type RunInfoMap = Record<string, RunInfo>;

export const extractResourceConfigs = (
  resourceConfigs: DefaultResourceConfigEdge[]
): Record<string, DefaultResourceConfig> => {
  const configs: Record<string, DefaultResourceConfig> = {};
  for (const edge of resourceConfigs) {
    configs[edge.node.id] = edge.node;
  }
  return configs;
};

export type RunQueueItem = {
  id: string;
  author: string | null;
  entityName: string;
  projectName: string;
  createdAt: string;
  updatedAt: string | null;
  associatedRunId: string | null; // Although the field says id this is a run name, e.g. 9nd6p3vf
  launchAgentId: string | null; // Is this type right?
  runSpec: Record<string, any>;
  state: string;
  error: Maybe<RunQueueItemInfoDetails> | undefined;
  warnings: Array<Maybe<RunQueueItemInfoDetails>>;

  versionedJob: string;
  jobName: string;
  alias: string;

  // After the initial query we will do additional queries
  // based on associatedRunId to populate this information.
  run: RunInfo | null;
  displayState: string;
  launchAgentName: string | null;
};

export type QueueStatus = 'active' | 'inactive'; // Active means at least one agent is polling
const ACTIVE_AGENT_STATES = ['POLLING', 'RUNNING'];

export type Queue = {
  entityName: string;
  projectName: string;
  id: string;
  name: string;
  createdAt: string;
  status: QueueStatus;
  items: RunQueueItem[];
  defaultResourceConfigID: string | null;
  config?: Json;
  resourceType: ResourceType;

  agents: Agent[];
};

const getDefaultResourceConfigMap = (
  scope: Entity | Project
): DefaultResourceConfigMap => {
  const map: DefaultResourceConfigMap = {};
  const drcEdges = scope?.defaultResourceConfigs?.edges ?? [];
  for (const drcEdge of drcEdges) {
    map[drcEdge.node.id] = drcEdge.node;
  }
  return map;
};

// Helper to extract the queue and agent information from a project.
// This is pulled out so we can use it on both the user's actual projects
// and the special wandb jobs project.
const extractQueuesAndAgentsFromProject = (
  entityName: string,
  projectName: string,
  queueFilter: string | null,
  proj: Project,
  agents: Agent[],
  drcMap: DefaultResourceConfigMap,
  queues: Queue[],
  queueMap: Record<string, Queue>
) => {
  const projAgents = proj?.launchAgents ?? [];
  for (const projAgent of projAgents) {
    agents.push(convertAgent(projAgent));
  }

  Object.assign(drcMap, getDefaultResourceConfigMap(proj));

  const projQueues = proj?.runQueues ?? [];
  for (const projQueue of projQueues) {
    if (queueFilter && projQueue.name !== queueFilter) {
      continue;
    }
    const defaultResourceConfigID = projQueue.defaultResourceConfigID ?? null;
    // TODO: Can this type be made more specific?
    let defaultResourceConfig: Record<string, any> = {};
    if (defaultResourceConfigID && defaultResourceConfigID in drcMap) {
      defaultResourceConfig = drcMap[defaultResourceConfigID];
    }

    const resourceType = defaultResourceConfig?.resource ?? 'unknown';
    const config = defaultResourceConfig?.config?.resource_args[resourceType];
    const queue: Queue = {
      entityName,
      projectName,
      id: projQueue.id,
      name: projQueue.name,
      createdAt: projQueue.createdAt.toString(),
      defaultResourceConfigID,
      items: extractRunQueueItems(
        projQueue.runQueueItems.edges as RunQueueItemEdge[]
      ),
      config,
      resourceType,

      // Temporary, will be corrected if necessary below
      status: 'inactive',
      agents: [],
    };
    queues.push(queue);
    queueMap[projQueue.id] = queue;
  }
};

const injectAgentInfoIntoQueues = (
  agents: Agent[],
  queueMap: Record<string, Queue>
) => {
  for (const agent of agents) {
    for (const queueId of agent.runQueues) {
      const q = queueMap[queueId];
      if (q) {
        q.agents.push(agent);
        if (ACTIVE_AGENT_STATES.includes(agent.agentStatus)) {
          q.status = 'active';
        }
      } else {
        // TODO: Why is this happening?
        // console.error(`Could not find queue ${queueId}`);
      }
    }
  }
};

const sortQueues = (queues: Queue[], personalEntity?: string) => {
  queues.sort((a, b) => {
    if (a.entityName === b.entityName) {
      return caseInsensitiveCompare(a.name, b.name);
    }
    // Sort wandb to top
    if (a.entityName === WANDB_ENTITY_NAME) {
      return -1;
    }
    if (b.entityName === WANDB_ENTITY_NAME) {
      return 1;
    }
    // Sort personal to bottom
    if (personalEntity != null && a.entityName === personalEntity) {
      return 1;
    }
    if (personalEntity != null && b.entityName === personalEntity) {
      return -1;
    }
    // Entity names are all lower case.
    return a.entityName.localeCompare(b.entityName);
  });
};

export const extractEntityQueues = (
  entityQueuesQuery: EntityQueuesQueryResult
): Queue[] => {
  const entity = entityQueuesQuery.data?.entity;
  const project = entityQueuesQuery.data?.project;
  if (entity == null || project == null) {
    return [];
  }
  const agents: Agent[] = [];
  const drcMap: DefaultResourceConfigMap = getDefaultResourceConfigMap(
    entity as Entity
  );
  const queues: Queue[] = [];
  const queueMap: Record<string, Queue> = {};

  extractQueuesAndAgentsFromProject(
    entity.name,
    project.name,
    null, // don't filter queues
    project as Project,
    agents,
    drcMap,
    queues,
    queueMap
  );

  injectAgentInfoIntoQueues(agents, queueMap);
  sortQueues(queues);

  return queues;
};

// Normalize query results, merging information about agents and default resource configs
// with information about queues.
export const extractQueues = (
  queryAll: AllQueuesQueryResult,
  personalEntity: string,
  includeProjectQueues: boolean,
  includeGlobalQueues: boolean
): Queue[] => {
  const agents: Agent[] = [];
  const drcMap: DefaultResourceConfigMap = {};
  const queues: Queue[] = [];
  const queueMap: Record<string, Queue> = {};

  const wandbEntity = queryAll.data?.entity;
  if (wandbEntity && includeGlobalQueues) {
    Object.assign(drcMap, getDefaultResourceConfigMap(wandbEntity as Entity));
  }

  let foundWandb = false;
  const teamEdges = queryAll.data?.viewer?.teams?.edges ?? [];
  for (const teamEdge of teamEdges) {
    const team = teamEdge.node;
    if (!team) {
      continue;
    }
    const entityName = team?.name ?? '';
    if (entityName === WANDB_ENTITY_NAME) {
      // User is a member of the wandb team, we don't want to add
      // a duplicate global queue again below.
      foundWandb = true;
    }

    Object.assign(drcMap, getDefaultResourceConfigMap(team as Entity));

    const projEdges = team?.projects?.edges ?? [];
    for (const projEdge of projEdges) {
      const proj = projEdge.node;
      const projectName = proj?.name ?? '';
      if (
        projectName !== DEFAULT_LAUNCH_PROJECT_NAME &&
        !includeProjectQueues
      ) {
        continue;
      }
      extractQueuesAndAgentsFromProject(
        entityName,
        projectName,
        null, // Don't filter queues
        proj as Project,
        agents,
        drcMap,
        queues,
        queueMap
      );
    }
  }

  // Add the special wandb jobs global queue for all users
  if (!foundWandb && includeGlobalQueues) {
    extractQueuesAndAgentsFromProject(
      WANDB_ENTITY_NAME,
      WANDB_JOBS_PROJECT_NAME,
      WANDB_GLOBAL_CPU_QUEUE, // Don't include any other wandb queues that may have been created
      queryAll.data?.project as Project,
      agents,
      drcMap,
      queues,
      queueMap
    );
  }

  // Now update queues with agent information
  injectAgentInfoIntoQueues(agents, queueMap);

  sortQueues(queues, personalEntity);
  return queues;
};

type JobFields = {
  // Entity and Project here refer to where the Job is defined. While that is the
  // default target entity/project for the created Run, the user can override.
  jobEntity: string;
  jobProject: string;
  versionedJob: string;
  jobName: string;
  alias: string;
};

// Parse job information (if available) into multiple fields to make it easier
// for downstream usage.
const extractJobFields = (job: string | undefined): JobFields => {
  const emptyJobFields = {
    jobEntity: '',
    jobProject: '',
    versionedJob: '',
    jobName: '',
    alias: '',
  };
  if (!job) {
    return emptyJobFields;
  }
  const [jobEntity, jobProject, versionedJob] = job.split('/');
  if (!jobEntity || !jobProject || !versionedJob) {
    return emptyJobFields;
  }
  const [jobName, alias] = versionedJob.split(':');
  return {
    jobEntity,
    jobProject,
    versionedJob,
    jobName,
    alias,
  };
};

export const extractRunQueueItems = (
  edges: RunQueueItemEdge[]
): RunQueueItem[] => {
  const items = [];
  for (const edge of edges) {
    const node = edge.node;
    const createdAt = node.createdAt.toString();

    // TODO: Old run queue items are supposed to be cleared out by a glue task
    //       but that isn't happening yet. Instead, we filter them out in the frontend for now.
    //       https://wandb.atlassian.net/browse/WB-11510
    const threshold = moment().subtract(7, 'days');
    if (moment(createdAt).isBefore(threshold) && node.state !== 'PENDING') {
      continue;
    }

    items.push({
      id: node.id,
      author: node.runSpec.author ?? null,
      entityName: node.runSpec.entity,
      projectName: node.runSpec.project,
      createdAt,
      updatedAt: node.createdAt.toString(),
      ...extractJobFields(node.runSpec.job),
      associatedRunId: node.associatedRunId ?? null,
      launchAgentId: node.launchAgentId ?? null,
      runSpec: node.runSpec,
      state: node.state,
      displayState: node.state, // Default, will override if we get run state info
      run: null,
      launchAgentName: null, // Default, will override if we get agent info
      error: node.error,
      warnings: node.warnings,
    });
  }
  return items;
};

// A somewhat arbitrary time limit because we can't currently distinguish
// between runs that crashed before wandb.init and runs that are still starting up.
const isNotRecent = (createdAt: string): boolean => {
  const threshold = moment().subtract(20, 'minutes');
  const created = moment.utc(createdAt, 'YYYY-MM-DDTHH:mm:ss').local();
  return created.isBefore(threshold);
};

// This method is used after we have queried for all of the details for associatedRunIds.
export const enhanceRunQueueItems = (
  items: RunQueueItem[],
  queue: Queue,
  runInfo: RunInfoMap
): RunQueueItem[] => {
  const enhanced = [];
  for (const item of items) {
    const {associatedRunId: runName, state: itemState} = item;
    item.launchAgentName =
      queue.agents?.find(a => a.id === item.launchAgentId)?.name ?? null;
    const run = runName && runInfo[runName] ? runInfo[runName] : null;

    // Update the display state based on run information
    // If none of the conditions apply, defaults to run queue item state.
    if (run) {
      item.displayState = run.state;
    } else if (itemState === 'PENDING') {
      item.displayState = queue.status === 'active' ? 'queued' : 'idle';
    } else if (itemState === 'CLAIMED' || itemState === 'LEASED') {
      if (isNotRecent(item.createdAt)) {
        item.displayState = 'crashed';
      } else {
        item.displayState = 'starting';
      }
    }
    enhanced.push({...item, run});
  }
  return enhanced;
};

// Iterate over a list of queues, building up a map from project
// to a list of associated run ids. Keys are in the format:
// <entity>/<project>
export const getProjectRunIdMap = (
  queues: Queue[]
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const queue of queues) {
    for (const item of queue.items) {
      if (!item.associatedRunId) {
        continue;
      }
      const key = `${item.entityName}/${item.projectName}`;
      if (!(key in map)) {
        map[key] = [];
      }
      map[key].push(item.associatedRunId);
    }
  }
  return map;
};

// Do one query per project name.
// Since we're looping over an unknown length list we can't use hooks.
// TODO: Have server expose a way of querying run id that isn't per-project.
export const getRunInformation = async (
  projectRunIdMap: Record<string, string[]>
) => {
  const runInfo: RunInfoMap = {};
  for (const [key, runIds] of Object.entries(projectRunIdMap)) {
    const [entityName, projectName] = key.split('/', 2);
    const filter = runNamesFilter(runIds);
    const filters = JSON.stringify(toMongo(filter));
    const response = await apolloClient.query({
      query: RUNS_INFORMATION_QUERY,
      variables: {
        entityName,
        projectName,
        filters,
      },
      fetchPolicy: 'no-cache',
    });
    if (!response.data.project) {
      // It is possible the run queue item references a project that was deleted.
      continue;
    }
    const runEdges = response.data.project.runs.edges ?? [];
    for (const runEdge of runEdges) {
      const {id, name, displayName, state, createdAt, heartbeatAt} =
        runEdge.node;
      runInfo[name] = {
        id,
        name,
        displayName,
        state,
        createdAt,
        heartbeatAt,
      };
    }
  }
  return runInfo;
};

export const isQueuePrioritizationEnabled = (
  queue: Pick<Queue, 'resourceType'>
): boolean => {
  // TODO(np): queue p12n mode is not yet available from the backend.  For now, assume
  // that only docker queues have p12n enabled.
  if (queue.resourceType === 'local-container') {
    return true;
  }

  return false;
};
